<template>
  <div class="text-sm cursor-default flex items-center w-full">
    <UserRoundCheck class="w-4 h-4 mr-2 flex-shrink-0" />
    <span class="font-medium mr-2 inline-block w-fit flex-shrink-0">Assigned referrer:</span>
    <div class="relative w-full">
      <Select
        v-model="selectedReferrerId"
        :disabled="referrersStore.isLoading || isUpdatingReferrer"
        class="w-full"
      >
        <SelectTrigger
          class="bg-white w-full text-sm disabled:opacity-50 disabled:cursor-not-allowed"
          :title="referrersStore.isLoading ? 'Loading...' : 'Select Referrer'"
        >
          <SelectValue placeholder="Assign Referrer..." />
        </SelectTrigger>
        <SelectContent class="w-full min-w-[var(--reka-select-trigger-width)]">
          <SelectItem value="unassigned" class="px-3">
            <span v-if="defaultReferrer"> Default referrer: {{ defaultReferrer?.name }} </span>
            <span v-else>No assigned referrer</span>
          </SelectItem>

          <hr class="my-1" />

          <!-- Company header if referrers exist -->
          <SelectLabel v-if="companyReferrers.length > 0" class="text-gray-600 cursor-default pt-1">
            {{ application?.open_role_details?.employer_details?.name }}
          </SelectLabel>

          <!-- Easy referrers -->
          <SelectGroup v-if="easyReferrers.length > 0" class="mb-2">
            <SelectLabel
              class="bg-green-100 rounded-md mb-1 text-green-600 cursor-default ml-2 px-3 py-1 font-medium"
              >Easy to reach</SelectLabel
            >
            <SelectItem
              v-for="item in easyReferrers"
              :key="item.id"
              :value="String(item.id)"
              class="ml-4 pl-4"
            >
              {{ item.name }}
            </SelectItem>
          </SelectGroup>

          <!-- Hard referrers -->
          <SelectGroup v-if="hardReferrers.length > 0" class="mb-2">
            <SelectLabel
              class="bg-red-100 rounded-md mb-1 text-red-600 cursor-default ml-2 px-3 py-1 font-medium"
              >Hard to reach</SelectLabel
            >
            <SelectItem
              v-for="item in hardReferrers"
              :key="item.id"
              :value="String(item.id)"
              class="ml-4 pl-4"
            >
              {{ item.name }}
            </SelectItem>
          </SelectGroup>

          <!-- Not specified referrers -->
          <SelectGroup v-if="notSpecifiedReferrers.length > 0" class="mb-2">
            <SelectLabel
              class="bg-gray-100 rounded-md mb-1 text-gray-600 cursor-default ml-2 px-3 py-1 font-medium"
            >
              Difficulty of reach not specified
            </SelectLabel>

            <SelectItem
              v-for="item in notSpecifiedReferrers"
              :key="item.id"
              :value="String(item.id)"
              class="ml-4 pl-4"
            >
              {{ item.name }}
            </SelectItem>
          </SelectGroup>

          <!-- No referrers message -->
          <p
            v-if="companyReferrers.length === 0"
            class="text-sm h-8 px-3 italic text-gray-500 cursor-default flex items-center"
          >
            No referring people found at
            {{ application?.open_role_details?.employer_details?.name }}
          </p>
        </SelectContent>
      </Select>
      <LoaderCircle
        v-if="isUpdatingReferrer"
        class="absolute right-8 top-1/2 transform -translate-y-1/2 w-4 h-4 animate-spin text-blue-500"
      />
    </div>
  </div>
  <div v-if="selectedReferrer?.notes" class="text-sm cursor-default w-full bg-green-100 rounded-md p-4 mt-2 border-[1px] border-green-600">
    <span class="font-medium text-green-900 mr-2 w-fit flex-shrink-0 flex items-center">
      
      <CircleAlert class="w-4 h-4 mr-1" />
      Internal notes for {{ selectedReferrer?.name }}</span>
    <p>{{ selectedReferrer?.notes }}</p>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { UserRoundCheck, LoaderCircle, CircleAlert } from 'lucide-vue-next'
import { useReferrersStore } from '@/stores/referrers'
import { useApplicationsStore } from '@/stores/applications'

// Shadcn Select Imports
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select'

const props = defineProps<{
  application: any
  applicationId: string
}>()

const emit = defineEmits<{
  referrerUpdated: []
}>()

const referrersStore = useReferrersStore()
const applicationsStore = useApplicationsStore()

// State for referrer dropdown
const selectedReferrerId = ref<string>('unassigned')
const selectedReferrer = computed(() => {
  return referrersStore.referrers.find((referrer) => referrer.id === Number(selectedReferrerId.value))
})
const isUpdatingReferrer = ref(false)

// Watch for application data changes to update selected referrer
watch(
  () => props.application,
  (newApp) => {
    if (newApp) {
      // Convert the ID to string for the Select v-model
      const referrerId = newApp.referrer
      selectedReferrerId.value =
        referrerId === null || referrerId === undefined ? 'unassigned' : String(referrerId)
    } else {
      selectedReferrerId.value = 'unassigned' // Reset if application is cleared
    }
  },
  { immediate: true, deep: true }
)

const companyReferrers = computed(() => {
  const referrers = referrersStore.referrers || []
  const employerId = props.application?.open_role_details?.employer
  return referrers
    .filter((referrer) => referrer.employers?.includes(employerId as number))
    .filter((referrer) => referrer.is_active)
})

const defaultReferrer = computed(() => {
  return companyReferrers.value.find((referrer) => {
    if (!referrer.default_referrer_for) return false
    return referrer.default_referrer_for.includes(
      props.application?.open_role_details?.employer as number
    )
  })
})

// Group referrers by reach difficulty
const easyReferrers = computed(() => {
  return companyReferrers.value.filter((referrer) => referrer.reachability === 'EASY')
})

const hardReferrers = computed(() => {
  return companyReferrers.value.filter((referrer) => referrer.reachability === 'HARD')
})

const notSpecifiedReferrers = computed(() => {
  return companyReferrers.value.filter((referrer) => !referrer.reachability)
})

// Watcher for selectedReferrerId changes (to trigger update)
watch(selectedReferrerId, async (newValStr, oldValStr) => {
  if (newValStr === oldValStr) return
  if (!props.application) return

  // Check if we're just setting the initial value
  const initialReferrerId = props.application.referrer
  const initialReferrerIdStr =
    initialReferrerId === null || initialReferrerId === undefined
      ? 'unassigned'
      : String(initialReferrerId)

  if (newValStr === initialReferrerIdStr) {
    // This is just the initial set, no need to update
    return
  }

  // Also check if the component is still loading the application
  if (applicationsStore.isLoadingSelected) return

  // Convert string value from select back to number | null
  let newReferrerIdForAPI: number | null
  if (newValStr === 'unassigned') {
    newReferrerIdForAPI = null
  } else {
    const parsed = parseInt(newValStr)
    newReferrerIdForAPI = isNaN(parsed) ? null : parsed
  }

  const applicationId = props.applicationId
  if (!applicationId) {
    console.error('Application ID not found for referrer update')
    return
  }

  // Check if there's actually a change to make
  const currentReferrerId = props.application?.referrer
  if (newReferrerIdForAPI === currentReferrerId) return

  isUpdatingReferrer.value = true
  try {
    await applicationsStore.updateApplication(applicationId, {
      referrer: newReferrerIdForAPI as any
    })

    // Re-fetch the application data
    await applicationsStore.fetchApplicationById(applicationId)

    // Emit event to notify parent of update
    emit('referrerUpdated')

    // Explicitly update selectedReferrerId based on the refetched data
    const updatedApplication = applicationsStore.selectedApplication
    const actualReferrerId = updatedApplication?.referrer
    if (actualReferrerId === null || actualReferrerId === undefined) {
      selectedReferrerId.value = 'unassigned'
    } else {
      selectedReferrerId.value = String(actualReferrerId)
    }
  } catch (error) {
    console.error('Failed to update referrer:', error)
    // Revert v-model state on error
    selectedReferrerId.value = typeof oldValStr === 'string' ? oldValStr : 'unassigned'
  } finally {
    isUpdatingReferrer.value = false
  }
})
</script>
